import {
  type PoolParams,
  useDeleteQuery,
  useGetQuery,
  usePatchQuery,
  usePostQuery,
  useRouteParams
} from "@quortex/front-common"
import { getTrashedAssetListUrl } from "@/queries/live2Vod/playL2VAssets"
import type { AssetCollectionParams, PaginationParams } from "@/queries/types"

//--------------------------------------------------------------------------------------------------
// URLs
//--------------------------------------------------------------------------------------------------

/**
 * @returns /pools/${poolUuid}/l2v/asset_collections/${assetUuid}?org=${orgUuid}
 */
export const getAssetCollectionUrl = ({
  orgUuid,
  poolUuid,
  assetCollectionUuid
}: Required<AssetCollectionParams>) => {
  const params = new URLSearchParams({ org: orgUuid })
  return `/pools/${poolUuid}/l2v/asset_collections/${assetCollectionUuid}/?${params}`
}

/**
 * @returns /pools/${poolUuid}/l2v/asset_collections/?org=${orgUuid}
 */
export const getAssetCollectionListUrl = ({
  orgUuid,
  poolUuid
}: Required<PoolParams> & PaginationParams) => {
  const params = new URLSearchParams({ org: orgUuid })
  return `/pools/${poolUuid}/l2v/asset_collections/?${params}`
}

/**
 * @returns /pools/${poolUuid}/l2v/tools/asset_collection_endpoints/?org=${orgUuid}
 */
export const getL2VCollectionWithEndpointsUrl = ({
  orgUuid,
  poolUuid
}: Required<PoolParams> & PaginationParams) => {
  const params = new URLSearchParams({ org: orgUuid })
  return `/pools/${poolUuid}/l2v/tools/asset_collection_endpoints/?${params}`
}
//--------------------------------------------------------------------------------------------------
// Queries & mutations
//--------------------------------------------------------------------------------------------------

export const useAssetCollection = (params?: AssetCollectionParams) => {
  const { orgUuid, poolUuid, assetCollectionUuid } = useRouteParams(params)

  const url = getAssetCollectionUrl({
    assetCollectionUuid,
    orgUuid,
    poolUuid
  })

  return useGetQuery<api.ott.L2VVODAssetCollection>("ott", url)
}

export const useAssetCollectionList = (params?: PoolParams & PaginationParams) => {
  const { orgUuid, poolUuid } = useRouteParams(params)
  const url = getAssetCollectionListUrl({ orgUuid, poolUuid })

  return useGetQuery<api.ott.PaginatedL2VVODAssetCollectionList>("ott", url)
}

export const useL2VCollectionWithEndpointList = (
  params?: AssetCollectionParams & PaginationParams
) => {
  const { orgUuid, poolUuid } = useRouteParams(params)
  const url = getL2VCollectionWithEndpointsUrl({ orgUuid, poolUuid })

  return useGetQuery<api.ott.PaginatedL2VToolsCollectionList>("ott", url)
}

export const useAddAssetCollection = (params?: PoolParams) => {
  const mergedParams = useRouteParams(params)
  const listUrl = getAssetCollectionListUrl(mergedParams)

  type TRequest = DeepPartial<api.ott.L2VVODAssetCollectionRequest>
  type TResponse = api.ott.L2VVODAssetCollection

  return usePostQuery<TRequest, TResponse>("ott", listUrl, [listUrl])
}

export const useUpdateAssetCollection = (params?: AssetCollectionParams) => {
  const mergedParams = useRouteParams(params)
  const url = getAssetCollectionUrl(mergedParams)
  const listUrl = getAssetCollectionListUrl(mergedParams)

  type TRequest = DeepPartial<api.ott.L2VVODAssetCollectionRequest>
  type TResponse = api.ott.L2VVODAssetCollection

  return usePatchQuery<TRequest, TResponse>("ott", url, [url, listUrl])
}

export const useDeleteAssetCollection = (params?: AssetCollectionParams) => {
  const { orgUuid, poolUuid, assetCollectionUuid } = useRouteParams(params)
  const assetCollectionUrl = getAssetCollectionUrl({ assetCollectionUuid, orgUuid, poolUuid })
  const trashedAssetListUrl = getTrashedAssetListUrl({ orgUuid, poolUuid })
  const listUrl = getAssetCollectionListUrl({ orgUuid, poolUuid })

  return useDeleteQuery("ott", assetCollectionUrl, [
    assetCollectionUrl,
    listUrl,
    trashedAssetListUrl
  ])
}
